/**
 * The entry point: it builds the `Host` — the four things the app cannot do
 * without a browser — and starts.
 *
 * Everything platform-specific is here, so the rest of `src/ui` can be read and
 * tested without one.
 */

import './ui/styles.css';
import { start } from './ui/app.js';
import {
  COMMIT_BUZZ_MS,
  UPDATE_CHECK_INTERVAL_MS,
  UPDATE_LOOK_ATTEMPTS,
  UPDATE_LOOK_INTERVAL_MS,
} from './config.js';
import { COPY } from './ui/copy.js';
import type { Copy } from './ui/copy.js';

/**
 * `10a` — the words the SHELL's own bars are in.
 *
 * Everything inside the app root reads through `CopyContext`; these five bars
 * cannot, because they live OUTSIDE it — fixed to the foot, offsetting the page
 * through `--prompt-h` — and are built in plain closures where no hook can
 * answer. So `start()` tells this file instead, through `onCopy`, once at boot
 * and again on every change.
 *
 * `let`, and it is the only mutable module state here. The alternative is
 * threading a `copy` parameter through four closures that are wired up before
 * the database has been read, which is where the language lives.
 */
let copy: Copy = COPY;

import { createBarSlot } from './ui/bar-slot.js';
import { barHooks, paintBar } from './ui/prompt-bar.js';
import type { BarSpec } from './ui/prompt-bar.js';

/**
 * §12 — `persist()` is three lines: call it and SURFACE `persisted()` HONESTLY.
 *
 * And §12's warning: "**do not treat private-mode detection as a dependable
 * gate.** There is no supported detection contract, and A SUCCESSFUL WRITE DOES
 * NOT PROVE PERSISTENCE. Report storage capability honestly; never label a
 * successful write as durable."
 *
 * So this asks, records the answer, and claims nothing further.
 */
async function askForPersistence(): Promise<boolean | null> {
  // THREE answers, not two, and the third is the reason this returns a union.
  // `null` is "this browser will not say" — the API is absent, or it threw.
  // Folding that into `false` would report a browser that keeps data perfectly
  // well as one that deletes it, and §12's rule cuts both ways: reporting
  // capability honestly forbids claiming danger as much as claiming durability.
  if (!('storage' in navigator) || typeof navigator.storage.persist !== 'function') return null;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

/**
 * §7.7.1 — "a download resolving proves THE DOWNLOAD WAS STARTED. It is the
 * strongest signal the platform offers, and it is still not completion — a
 * download can be cancelled or fail afterwards, and nothing reports that back."
 *
 * The counter that reads this says only what this function knows.
 */
function download(name: string, type: string, contents: string): void {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Revoked on the next turn, so the download has taken its reference.
  setTimeout(() => { URL.revokeObjectURL(url); }, 0);
}

function pickFile(): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json,.json';
    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }
      void file.text().then(resolve, () => { resolve(null); });
    });
    input.addEventListener('cancel', () => { resolve(null); });
    input.click();
  });
}

/**
 * §11.4 — the prompt protocol, page side, including **waiting-worker detection
 * at startup**: a worker can already be waiting when the page loads, and a page
 * that only listens for `updatefound` never notices.
 */

/**
 * §11.4's prompts, as ONE component — the update offer and the install offer
 * were two copies of the same markup with the same two defects.
 *
 * **It was prepended to `<body>`, so it displaced the entire page downward.**
 * Momin reported it as the app being pushed off centre. A prompt is an
 * interruption; it should sit over the page, not rearrange it.
 *
 * **Fixed to the FOOT, not the head.** Same argument that moved Settings and
 * History: the top of a one-handed phone is the hardest place to reach, and
 * this is a control the user is being asked to act on.
 *
 * **The page keeps its own bottom clear.** A fixed bar at the bottom would sit
 * on top of the primary action — "Work out the dose", "Log this injection" —
 * which is the one control it must never cover. So the bar's height is
 * published as a custom property and `#app` pads by it while a prompt is up.
 * That is the difference between overlaying the PAGE and overlaying a BUTTON.
 */

const bars = createBarSlot<BarSpec>((spec, gone) => paintBar(spec, gone));
// The SAME builders the tests wire, so a test cannot pass against a fake.
const hooks = barHooks(
  (kind, spec) => { bars.raise(kind, spec); },
  (kind) => { bars.retire(kind); },
  () => copy,
  // Into the same map `update` and `install` use, so `onCopy` repaints these
  // three as well. They had no entry at all, which is how a failed-write bar
  // could sit in English under an Urdu interface.
  (kind, paint) => { redraw.set(kind, paint); },
);

/**
 * How a standing bar learns the language, keyed by kind.
 *
 * `10a` taught these five bars to READ their words from `copy`; it did not
 * teach them to read them AGAIN. They are raised once and latched — `offered`
 * and `shown` exist so a bar cannot stack on itself — and the update offer is
 * raised from `whenLoaded`, which runs SYNCHRONOUSLY on a revisit served from
 * cache. That is before `start()` has opened the database, so before the
 * language is known.
 *
 * It was visible on the deployed build: an install offer reading "Add this to
 * your home screen?" under an interface in Urdu. On screen, not theorised.
 *
 * Only the bar `bars.showing()` names is repainted, which is what keeps a
 * DISMISSED one from being resurrected by a language change. The limit that
 * leaves: a bar sitting in the queue behind another keeps the words it was
 * raised with until it surfaces. One bar at a time is the normal case and the
 * queue is rarely deeper than one, so this is stated rather than solved.
 */
const redraw = new Map<string, () => void>();


function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;

  let offered = false;
  const offer = (waiting: ServiceWorker): void => {
    // `offerIfReady` is called from four places on purpose. Idempotent, or the
    // page grows a stack of identical bars.
    if (offered) return;
    offered = true;
    const paint = (): void => {
      bars.raise('update', {
        text: copy.update.ready,
        actionLabel: copy.update.useNow,
        onAction: () => { waiting.postMessage({ type: 'SKIP_WAITING' }); },
        dismissLabel: copy.update.later,
      });
    };
    redraw.set('update', paint);
    paint();
  };

  /**
   * Runs on `load`, or IMMEDIATELY if load has already happened.
   *
   * `window.addEventListener('load', ...)` alone never fires when the document
   * is already complete by the time this module executes — and on a revisit
   * served entirely from the service-worker cache, that is exactly what can
   * happen. Diagnosed by exposing the registration callback's own state: on a
   * first visit it reported `reachedThen: true` with eight looks; on a revisit
   * it reported NOTHING AT ALL. The handler had never run, so nothing
   * registered, nothing checked for an update, and no prompt could appear —
   * which is why a phone stayed on the build it first cached.
   */
  const whenLoaded = (run: () => void): void => {
    if (document.readyState === 'complete') {
      run();
      return;
    }
    window.addEventListener('load', run);
  };

  whenLoaded(() => {
    // Read BEFORE registering. `controllerchange` fires both when a NEW worker
    // replaces an old one — the case §11.4's reload exists for — and when the
    // very FIRST worker claims a page that had none. Only the first of those
    // needs a reload.
    const hadController = navigator.serviceWorker.controller !== null;
    let lastUpdateCheck = Date.now();

    void navigator.serviceWorker
      .register(`${import.meta.env.BASE_URL}sw.js`, { scope: import.meta.env.BASE_URL })
      .then((registration) => {
        /**
         * ONE place that decides whether to offer, called from every point a
         * new worker can become available — because each of them alone misses
         * cases:
         *
         *   * at registration, `waiting` is usually null: the update has not
         *     run yet.
         *   * `updatefound` reads `registration.installing`, which can ALREADY
         *     have moved on to `waiting` by the time the handler runs.
         *
         * Measured: a revisit after a new build reported `waiting: "installed"`
         * — the worker was there and ready — and no prompt appeared, because
         * every individual hook had looked at the wrong moment. So the check is
         * idempotent and is run repeatedly instead of being placed perfectly.
         */
        const offerIfReady = (): void => {
          const next = registration.waiting;
          // `controller` is the guard against offering on a FIRST install,
          // where there is no previous version to replace (see note 44).
          if (next && navigator.serviceWorker.controller) offer(next);
        };
        offerIfReady();

        /**
         * And LOOK, rather than trusting an event to fire at a useful moment.
         *
         * Every hook above can miss. At registration `waiting` is usually null;
         * `updatefound` may fire before this code runs; `installing` can already
         * have moved to `waiting`; and `update()` can resolve while the new
         * worker is still installing. Measured with all four wired:
         * `waiting: "installed"` and **no prompt on screen** — the worker was
         * ready and nothing had looked at the right time.
         *
         * `registration.waiting` is directly observable, so this looks at it a
         * bounded number of times and stops. Polling is the crude answer and it
         * is the one that cannot miss, which for the mechanism that tells a
         * person their dosing app is out of date is the right trade.
         */
        let looks = 0;
        const watch = setInterval(() => {
          looks += 1;
          offerIfReady();
          if (offered || looks >= UPDATE_LOOK_ATTEMPTS) clearInterval(watch);
        }, UPDATE_LOOK_INTERVAL_MS);

        /**
         * ASK. Registering does not check whether a newer worker exists, and
         * §11.4's prompt protocol is worthless if nothing ever triggers it.
         *
         * Measured: install a build, ship a new one, revisit — `waiting` and
         * `installing` were both null. Nothing had asked, so the new worker was
         * not even installing yet, and the offer could not exist.
         *
         * This does NOT mean a cached build is permanent — an earlier version of
         * this comment claimed that and it was wrong. A waiting worker activates
         * once every client is gone, so closing and reopening the app delivers
         * the new build (measured; see BACKLOG T4). What the check buys is
         * NOTICING SOONER, without waiting for a navigation that an installed
         * PWA may not make for a long time.
         *
         * The browser does check on a navigation within scope, but an INSTALLED
         * PWA is opened and left open — it may go days without one, which is
         * exactly the case this app is built for.
         */
        void registration.update().then(offerIfReady, () => {
          // Offline, most likely. §10.8 — never gate the app on connectivity.
        });
        document.addEventListener('visibilitychange', () => {
          // On coming back to the app, which is when a phone actually resumes.
          // Throttled, because this fires on every tab switch and each call is
          // a network request.
          if (document.visibilityState !== 'visible') return;
          const now = Date.now();
          if (now - lastUpdateCheck < UPDATE_CHECK_INTERVAL_MS) return;
          lastUpdateCheck = now;
          void registration.update().then(offerIfReady, () => { /* offline */ });
        });
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          // Already past `installing`; `offerIfReady` will see it in `waiting`.
          if (!installing) {
            offerIfReady();
            return;
          }
          installing.addEventListener('statechange', offerIfReady);
        });
      })
      .catch(() => {
        // §10.8 — never gate the app on this. The math is local, and an app that
        // will not open offline is a worse outcome than one without an update
        // prompt.
      });

    // The activation acknowledgement: the new worker took control, so reload
    // once onto a coherent set of HTML, assets and worker (§11.4).
    //
    // GUARDED, and the guard is the whole point. Without `hadController` this
    // also fired on a FIRST visit, when the initial worker claims a page that
    // had no controller — and then the app reloaded ITSELF mid-session, for
    // nothing. Measured: a cold profile recorded TWO document loads, a warm one
    // recorded one.
    //
    // What that looked like in use: tap "Log this injection", and if the reload
    // landed on that moment the screen went blank and the tap appeared to be
    // ignored. The row was written — the reload just threw away the screen that
    // would have said so. Momin reported it as the app being stuck on his
    // phone, and the console was clean because nothing had gone wrong.
    //
    // On a first visit there is nothing incoherent to fix: the page already
    // loaded the assets it was served, and the worker has only just started
    // caching them. §11.4 wants coherence AFTER AN UPDATE, which is exactly
    // what `hadController` distinguishes.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadController) return;
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

/**
 * §12 — the install prompt is **DOWNGRADED TO A LIGHT TOUCH**: "an ordinary
 * `beforeinstallprompt` flow rather than an onboarding gate."
 *
 * Version 1 gated onboarding on installing first, for two reasons that §12
 * establishes **do not apply to this user**: Android Chrome does not evict
 * storage after seven days, and its generated package shares origin storage, so
 * data does transfer on install. Both facts were iOS facts.
 */
function offerInstall(): { routine: () => void; atRisk: (show: boolean) => void } {
  let pending: (Event & { prompt: () => Promise<void> }) | null = null;
  let shown = false;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep it from appearing on its own; offer it where he can see it, at a
    // moment when it is not standing on top of something that matters.
    event.preventDefault();
    pending = event as Event & { prompt: () => Promise<void> };
  });

  return {
    /**
     * The ordinary offer, when the browser can be ASKED. One tap installs.
     */
    routine: (): void => {
      const prompt = pending;
      if (prompt === null || shown) return;
      shown = true;
      const paint = (): void => {
        bars.raise('install', {
          text: copy.install.offer,
          actionLabel: copy.install.add,
          onAction: () => { void prompt.prompt(); },
          dismissLabel: copy.install.notNow,
        });
      };
      redraw.set('install', paint);
      paint();
    },
    /**
     * §12 — the same offer, when the browser has not promised to keep the
     * record. `shown` is shared with `routine` deliberately: these are two
     * shapes of one message, and the slot holds one bar, so raising both would
     * mean the second displacing the first for no reason.
     *
     * Where a real install prompt exists, prefer it — one tap beats three.
     * Where it does not, which is every browser on an iPhone, the text carries
     * the taps and there is nothing to put behind a button.
     */
    atRisk: (show: boolean): void => {
      // Retire it rather than merely stop raising it. The guard in `app.tsx`
      // prevented the bar being RAISED on Settings and did nothing about one
      // already on screen, so a bar raised on the calculator followed the
      // reader to the screen that explains it in full — the condition was
      // fixed and the state that outlives it was not.
      if (!show) {
        if (shown) bars.retire('install');
        return;
      }
      // `shown` is never reset: retiring it on Settings must not re-raise it
      // when the reader walks back to the calculator.
      if (shown) return;
      shown = true;
      const prompt = pending;
      if (prompt !== null) {
        bars.raise('install', {
          text: copy.install.offer,
          actionLabel: copy.install.add,
          onAction: () => { void prompt.prompt(); },
          dismissLabel: copy.install.notNow,
        });
        return;
      }
      bars.raise('install', {
        // The steps are in the TEXT. Behind a button they would need a second
        // bar, which is the stacking this arrangement exists to avoid — and on
        // WebKit there is nothing else a button could do.
        text: `${copy.storage.atRiskBar} ${copy.installSteps(navigator.maxTouchPoints)} ${copy.storage.barOptOut}`,
        dismissLabel: copy.storage.atRiskDismiss,
        variant: 'warn',
      });
    },
  };
}

/**
 * §10.7's back gesture, and BACKLOG 24's four addresses, in one mechanism.
 *
 * **The URL is a projection of `state.screen`.** `machine.ts` owns where the app
 * is; this writes that outwards and reports the browser's navigations back in.
 * Nothing here decides anything — that is the whole point, and it is why there
 * is no router library. A router wants to own "where is the app", and a second
 * owner of that question is the two-sources-of-truth defect this project keeps
 * refusing.
 *
 * **Why the sentinel survives.** Real routes let the browser do the work for the
 * four screens that have addresses. The calculator's steps deliberately do not
 * have one — §8.2 expires a result, and a URL that restores a screen restores a
 * dose — so back INSIDE the wizard still needs an entry that carries no address.
 * `BACKLOG` 24 expected routing to delete this function outright; it does not,
 * because the safety exclusion that keeps the calculator unroutable is exactly
 * what keeps the sentinel necessary.
 *
 * The pushed entry replaces its predecessor's documented defect rather than
 * inheriting it: there is still no `history.back()` call here, so nothing
 * navigates on this function's own bookkeeping.
 */
function browserHistory(): {
  syncHistory: (can: boolean, path: string) => void;
  onNavigate: (handler: (path: string) => void) => void;
} {
  let sentinel = false;
  let handler: ((path: string) => void) | null = null;

  window.addEventListener('popstate', () => {
    // The browser has already moved by the time this runs.
    sentinel = false;
    handler?.(window.location.pathname);
  });

  return {
    syncHistory: (can, path) => {
      // A different ADDRESS is a real navigation and always gets an entry, so
      // back leaves the route the way it arrived and the address bar agrees
      // with the screen.
      if (window.location.pathname !== path) {
        history.pushState(null, '', path);
        sentinel = true;
        return;
      }
      // Same address: the wizard. PUSH ONLY, and deliberately no branch for
      // `!can` — see above. A press on a no-back screen is absorbed by the
      // handler doing nothing, and the next one leaves the app.
      if (can && !sentinel) {
        history.pushState(null, '', path);
        sentinel = true;
      }
    },
    onNavigate: (next) => {
      handler = next;
    },
  };
}

const root = document.querySelector<HTMLDivElement>('#app');
if (root) {
  // NOT discarded — §12 says "call it and SURFACE `persisted()` honestly", and
  // until 2026-09-18 this was `void askForPersistence()`, which did the calling
  // and none of the surfacing. The app awaits it and renders when it lands, so
  // boot is not held up by a storage API.
  const storagePersisted = askForPersistence();
  registerServiceWorker();
  const installOffer = offerInstall();
  void start({
    root,
    now: () => Date.now(),
    // §10.4 — the DEVICE's zone, ruled 2026-09-11 and no longer open. Nothing
    // dose-bearing reads it: §7.4's windows, §7.6's skew and §8.2's expiry are
    // pure epoch arithmetic, so travel and DST cannot move a dose or a gate.
    // Read ONCE, which is the accepted cost: until the app restarts, a real
    // zone change leaves rendered times in the old zone. Notes 11 and 21.
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    appVersion: __APP_VERSION__,
    buildId: __BUILD_ID__,
    download,
    pickFile,
    /**
     * §7.2 — the commit buzz. Feature-detected because the fallback is
     * genuinely fine: no vibration at all, with the screen still saying what
     * happened. That is the case where detection earns its keep, unlike
     * `crypto.randomUUID` where it would only have hidden a defect.
     *
     * iOS Safari does not implement this and will simply not buzz.
     */
    buzz: () => {
      if (typeof navigator.vibrate !== 'function') return;
      try {
        navigator.vibrate(COMMIT_BUZZ_MS);
      } catch {
        // Some browsers throw without a prior user gesture. The tap that got
        // here IS one, but a confirmation must never be able to break a commit.
      }
    },
    scrollY: () => window.scrollY,
    scrollTo: (y) => { window.scrollTo(0, y); },
    storagePersisted,
    setTitle: (title) => {
      if (document.title !== title) document.title = title;
    },
    ...browserHistory(),
    initialPath: window.location.pathname,
    onSettled: installOffer.routine,
    onStorageAtRisk: installOffer.atRisk,
    // §7.2 — the write and its automatic retry have both failed, so the dose is
    // not in the record and he is the only one who can carry it. A bar rather
    // than a line on the logged screen because `committing` outlives that
    // screen now: by the time this fires he may be two screens away.
    // `10a` — the bars above are outside the app root and no provider reaches
    // them. This is how they learn the language, and the redraw is why they
    // learn it even when they were raised before the database was read.
    onCopy: (next) => {
      copy = next;
      // EVERY bar in the queue, not only the one on screen. Repainting the
      // standing one alone left the rest holding the words they were raised
      // with, so dismissing the top bar surfaced English under an Urdu
      // interface — the same defect §10a fixed for the standing bar, which
      // never followed into the queue. Repainting a queued kind only replaces
      // the spec it will be painted from; nothing is torn down.
      for (const kind of bars.queued()) redraw.get(kind)?.();
    },
    ...hooks,
  }).catch((cause: unknown) => {
    // `COPY`, deliberately, not `copy`. This fires when `start()` itself
    // rejected — so the database was never read, no language was ever loaded,
    // and `copy` is still English anyway. Naming the constant says that rather
    // than implying a choice was made.
    root.textContent = `${COPY.couldNotStart(COPY.appName)}${String(cause)}`;
  });
}
